import SEO from "../components/SEO";

export default function Privacy() {
  const privacySchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Privacy Policy",
    "url": "https://www.thrombio.com.au/privacy",
    "publisher": {
      "@type": "Organization",
      "name": "ThromBio Holdings Pty Ltd",
      "url": "https://www.thrombio.com.au/",
      "contactPoint": {
        "@type": "ContactPoint",
        "email": "info@thrombio.com.au"
      }
    }
  };

  return (
    <>
      <SEO
        title="Privacy Policy"
        description="How ThromBio collects, uses, and protects personal information through the thrombio.com.au website."
        keywords={['Privacy Policy', 'Data Protection', 'Australian Privacy Principles', 'Cookies', 'Analytics']}
        schema={privacySchema}
      />

      <div className="bg-[#010C0E]/10">
        <div className="page-width px-12 md:px-20 pt-[120px] md:pt-[95px] pb-40">
          <div className="max-w-[860px] mx-auto flex flex-col gap-10">
            <div>
              <h1 className="font-bold text-[4.8rem] leading-[5.2rem] tracking-[.192rem] text-[#C3EEFD]">
                Privacy Policy
              </h1>
              <p className="font-sans text-[1.6rem] leading-[2.4rem] mt-6 opacity-80">
                Last updated: 22 June 2026
              </p>
            </div>

            <p className="font-sans text-[1.85rem] leading-[2.7rem]">
              ThromBio Holdings Pty Ltd (ACN 642 009 952, ABN 28 642 009 952) ("ThromBio", "we",
              "us", "our") is committed to protecting the privacy of visitors to our website at{" "}
              <a href="https://www.thrombio.com.au" className="text-primary-blue hover:underline">
                https://www.thrombio.com.au
              </a>{" "}
              (the "Website"). This policy explains what personal information we collect through the
              Website, how we use and protect it, and the choices and rights you have.
            </p>

            <p className="font-sans text-[1.85rem] leading-[2.7rem]">
              This policy applies to the Website only. It does not cover information handled outside
              the Website, including in connection with clinical trials, investor relations, or
              employment, which are addressed separately.
            </p>

            {/* 1 */}
            <section className="flex flex-col gap-5">
              <h2 className="font-syne font-bold text-[2.6rem] leading-[3.6rem]">
                1. The information we collect
              </h2>
              <p className="font-sans text-[1.85rem] leading-[2.7rem]">
                <span className="font-bold">Information you provide to us.</span> When you use the
                contact form on our Website, we collect the information you choose to submit — your
                name, email address, company (optional), and the content of your message.
              </p>
              <p className="font-sans text-[1.85rem] leading-[2.7rem]">
                <span className="font-bold">Information collected automatically.</span> When you
                visit the Website, certain information is collected automatically by analytics
                tools, including your IP address (which may be truncated or anonymised by the
                analytics provider), browser and device type, pages viewed, referring website, and
                the dates and times of your visits. We collect this using:
              </p>
              <ul className="font-sans text-[1.85rem] leading-[2.7rem] list-disc pl-10 flex flex-col gap-3">
                <li>
                  <span className="font-bold">Google Analytics 4</span> and{" "}
                  <span className="font-bold">Google Tag Manager</span>, provided by Google, to
                  understand how visitors use the Website and to improve it.
                </li>
                <li>
                  <span className="font-bold">Cookies and similar technologies</span> — see section 4.
                </li>
              </ul>
            </section>

            {/* 2 */}
            <section className="flex flex-col gap-5">
              <h2 className="font-syne font-bold text-[2.6rem] leading-[3.6rem]">
                2. How we use your information
              </h2>
              <p className="font-sans text-[1.85rem] leading-[2.7rem]">
                We use the information we collect to:
              </p>
              <ul className="font-sans text-[1.85rem] leading-[2.7rem] list-disc pl-10 flex flex-col gap-3">
                <li>respond to enquiries you submit through the contact form;</li>
                <li>
                  operate, maintain, and improve the Website and understand how it is used;
                </li>
                <li>maintain the security and integrity of the Website;</li>
                <li>comply with our legal obligations.</li>
              </ul>
              <p className="font-sans text-[1.85rem] leading-[2.7rem]">
                We do <span className="font-bold">not</span> sell your personal information.
              </p>
            </section>

            {/* 3 */}
            <section className="flex flex-col gap-5">
              <h2 className="font-syne font-bold text-[2.6rem] leading-[3.6rem]">
                3. Who we share it with
              </h2>
              <p className="font-sans text-[1.85rem] leading-[2.7rem]">
                We share personal information only with service providers who help us operate the
                Website, and only as needed for them to perform their services. These include:
              </p>
              <ul className="font-sans text-[1.85rem] leading-[2.7rem] list-disc pl-10 flex flex-col gap-3">
                <li>
                  <span className="font-bold">Google</span> (Google Analytics 4, Google Tag
                  Manager, and Google Fonts) — website analytics and web fonts.
                </li>
                <li>
                  <span className="font-bold">Google Cloud Platform</span> — processes contact-form
                  submissions and delivers them to us by email.
                </li>
                <li>
                  <span className="font-bold">Vercel</span> — website hosting and content delivery.
                </li>
              </ul>
              <p className="font-sans text-[1.85rem] leading-[2.7rem]">
                {/* LEGAL REVIEW: confirm cross-border disclosure wording and destination countries. */}
                Some of these providers — including Google and Vercel — may store or process data
                outside Australia, including in the United States. Where this occurs, we take
                reasonable steps to ensure your information is handled consistently with the
                Australian Privacy Principles.
              </p>
              <p className="font-sans text-[1.85rem] leading-[2.7rem]">
                We may also disclose information where required by law, or to protect our rights or
                the safety of others.
              </p>
            </section>

            {/* 4 */}
            <section className="flex flex-col gap-5">
              <h2 className="font-syne font-bold text-[2.6rem] leading-[3.6rem]">
                4. Cookies and analytics
              </h2>
              <p className="font-sans text-[1.85rem] leading-[2.7rem]">
                The Website uses cookies and similar technologies, primarily for analytics via
                Google Analytics 4 and Google Tag Manager. These help us understand site usage and
                improve the Website.
              </p>
              <p className="font-sans text-[1.85rem] leading-[2.7rem]">
                You can control or disable cookies through your browser settings. Disabling cookies
                may affect some Website functionality.
              </p>
            </section>

            {/* 5 */}
            <section className="flex flex-col gap-5">
              <h2 className="font-syne font-bold text-[2.6rem] leading-[3.6rem]">
                5. How long we keep it
              </h2>
              <p className="font-sans text-[1.85rem] leading-[2.7rem]">
                We retain personal information only for as long as necessary for the purposes
                described in this policy or as required by law.
              </p>
              <ul className="font-sans text-[1.85rem] leading-[2.7rem] list-disc pl-10 flex flex-col gap-3">
                <li>
                  Contact-form submissions: sent to us by email and retained in a shared mailbox on
                  an ongoing basis.
                </li>
                <li>
                  Analytics data: retained according to the retention settings configured in Google
                  Analytics 4, currently 14 months.
                </li>
              </ul>
            </section>

            {/* 6 */}
            <section className="flex flex-col gap-5">
              <h2 className="font-syne font-bold text-[2.6rem] leading-[3.6rem]">6. Security</h2>
              <p className="font-sans text-[1.85rem] leading-[2.7rem]">
                We take reasonable steps to protect personal information from misuse, interference,
                loss, and unauthorised access, modification, or disclosure. No method of
                transmission over the internet is completely secure, however, and we cannot
                guarantee absolute security.
              </p>
            </section>

            {/* 7 */}
            <section className="flex flex-col gap-5">
              <h2 className="font-syne font-bold text-[2.6rem] leading-[3.6rem]">7. Your rights</h2>
              <p className="font-sans text-[1.85rem] leading-[2.7rem]">
                Under the Australian Privacy Act, you may request access to the personal information
                we hold about you and ask us to correct it if it is inaccurate. To make a request,
                or to ask a question about this policy, contact us using the details in section 9.
              </p>
              {/* LEGAL REVIEW: confirm whether additional EU/UK (GDPR) visitor rights statements are required, given the site is internationally accessible. */}
              <p className="font-sans text-[1.85rem] leading-[2.7rem]">
                If you are located in the European Union or United Kingdom, you may have additional
                rights under applicable data protection law, including rights to access, correct,
                erase, or restrict the processing of your personal information. To exercise any of
                these rights, contact us using the details in section 9.
              </p>
            </section>

            {/* 8 */}
            <section className="flex flex-col gap-5">
              <h2 className="font-syne font-bold text-[2.6rem] leading-[3.6rem]">8. Children</h2>
              {/* LEGAL REVIEW: confirm appropriate age threshold and wording. */}
              <p className="font-sans text-[1.85rem] leading-[2.7rem]">
                The Website is not directed at children under 18, and we do not knowingly collect
                personal information from children. If you believe a child has provided us with
                personal information, please contact us so we can remove it.
              </p>
            </section>

            {/* 9 */}
            <section className="flex flex-col gap-5">
              <h2 className="font-syne font-bold text-[2.6rem] leading-[3.6rem]">
                9. How to contact us
              </h2>
              <p className="font-sans text-[1.85rem] leading-[2.7rem]">
                If you have any questions about this policy, or wish to make a privacy request or
                complaint, contact:
              </p>
              <p className="font-sans text-[1.85rem] leading-[2.7rem]">
                ThromBio Holdings Pty Ltd
                <br />
                Email:{" "}
                <a href="mailto:info@thrombio.com.au" className="text-primary-blue hover:underline">
                  info@thrombio.com.au
                </a>
              </p>
              <p className="font-sans text-[1.85rem] leading-[2.7rem]">
                If you are not satisfied with our response, you may escalate your complaint to the
                Office of the Australian Information Commissioner (OAIC) at{" "}
                <a
                  href="https://www.oaic.gov.au"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-blue hover:underline"
                >
                  www.oaic.gov.au
                </a>
                .
              </p>
            </section>

            {/* 10 */}
            <section className="flex flex-col gap-5">
              <h2 className="font-syne font-bold text-[2.6rem] leading-[3.6rem]">
                10. Changes to this policy
              </h2>
              <p className="font-sans text-[1.85rem] leading-[2.7rem]">
                We may update this policy from time to time. The current version is always available
                on this page, with the "Last updated" date above reflecting the most recent change.
              </p>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
